import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { colors } from '@guryou/colors';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
// import SignatureCanvas from 'react-signature-canvas';
import {
  stateIsLoaded,
  stateIsLoading,
  StateStatus,
  updateCustomerDataFromInvitationFlow,
  getFlowSignatureUploadUrl,
  getFileFromCustomerFlow,
  useEffectOnSuccess,
  resetGetFileState,
  uploadMultipleFilesToS3,
  cognitoLogout,
} from '@guryou/redux';
import { generateDynamicSchema } from './generateSchemaHelper';
import { StepTypes, FileTypes } from '@guryou/core';
import { FullScreenSpinner } from '@guryou/html-components';
import { printPercent } from '@guryou/utils/build/stringUtils';
import { useTranslation } from 'react-i18next';
import { getFileFromCustomerFlowApiCall } from '@guryou/api-client-gql';

const getDocumentRequests = step => {
  if (Array.isArray(step.documents)) return step.documents;
  if (step.documentName) {
    return [
      {
        documentName: step.documentName,
        documentType: step.documentType || '',
        includeDescription: step.includeDescription || false,
        descriptionRequired: step.descriptionRequired || false,
        includeExpirationDate: step.includeExpirationDate || false,
        expirationDateRequired: step.expirationDateRequired || false,
        documentRequired: step.documentRequired || false,
      },
    ];
  }
  return [];
};

const FunctioningStepper = ({ flowData, email }) => {
  const { t } = useTranslation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [progressPercent, setProgressPercent] = useState(null);
  const { upload } = useSelector(state => state.files);

  const signatureRef = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [signatureError, setSignatureError] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const updateSelector = useSelector(state => state.customersInvitationFlow.updateCustomerDataFromFlow);

  const dynamicSchema = useMemo(() => generateDynamicSchema(flowData, t), [flowData, t]);

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(dynamicSchema),
    mode: 'onTouched',
  });

  useEffect(() => {
    if (filesToUpload.length > 0 && stateIsLoaded(updateSelector) && updateSelector.data?.presignedUrls) {
      const fileUrlPairs = updateSelector.data.presignedUrls
        .map((pu, idx) => {
          const fileIndex = pu.fileIndex !== undefined ? pu.fileIndex : idx;
          let file = filesToUpload[fileIndex];

          // Double check by file name just to be safe
          if (file && pu.fileName && file.name !== pu.fileName) {
            file = filesToUpload.find(f => f.name === pu.fileName) || file;
          }

          return file ? { file, url: pu.presignedUrl } : null;
        })
        .filter(pair => pair && pair.url);

      if (fileUrlPairs.length > 0) {
        setProgressPercent(0);
        dispatch(
          uploadMultipleFilesToS3(fileUrlPairs, pp => {
            setProgressPercent(pp);
          })
        );
      }
    }
    //eslint-disable-next-line
  }, [updateSelector.version, dispatch]);

  useEffect(() => {
    if (stateIsLoaded(upload)) {
      setProgressPercent(null);
    }
  }, [dispatch, upload, updateSelector]);

  useEffect(() => {
    if (updateSelector.status === StateStatus.ERROR) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider')) {
          localStorage.removeItem(key);
        }
      });
      dispatch(cognitoLogout());
    }
  }, [dispatch, updateSelector.status]);

  useEffectOnSuccess(
    state => state.files.getFlowFile,
    data => {
      if (data?.type === FileTypes.document) {
        window.open(data?.signedUrl, '_blank');
      }
      dispatch(resetGetFileState());
    }
  );

  if (!flowData || !flowData.steps || flowData.steps.length === 0) {
    return <div className="p-6 text-center text-gray-500">{t('INVITE_FLOW.noFormData', 'No form data provided.')}</div>;
  }

  const steps = flowData.steps;
  const currentStep = steps[currentStepIndex];
  const stepTypeInfo = StepTypes[currentStep.type] || {};

  const handleNext = async () => {
    const fieldsToValidate = [];

    if (currentStep.fields) fieldsToValidate.push(...Object.keys(currentStep.fields));
    if (currentStep.questions) fieldsToValidate.push(...currentStep.questions.map(q => String(q.id)));

    if (currentStep.type === 'documents') {
      const docRequests = getDocumentRequests(currentStep);
      docRequests.forEach((_, di) => {
        fieldsToValidate.push(`documentFile_${di}`);
        fieldsToValidate.push(`documentDescription_${di}`);
        fieldsToValidate.push(`documentExpirationDate_${di}`);
      });
    }

    if (currentStep.type === 'agreement') {
      fieldsToValidate.push('agreement_accepted');
    }

    const isStepValid = await trigger(fieldsToValidate);

    if (isStepValid && currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const typedSignatureToDataUrl = async name => {
    await document.fonts.load('52px "Dancing Script"');
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 500, 200);
    ctx.font = '52px "Dancing Script", cursive';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 250, 100);
    return canvas.toDataURL('image/png');
  };

  const onSubmit = async data => {
    const enrichedQuestions = [];
    const questionIds = [];

    flowData.steps.forEach(step => {
      if (step.type === 'questions' && step.questions) {
        step.questions.forEach(q => {
          const qIdStr = String(q.id);
          questionIds.push(qIdStr);
          const answer = data[qIdStr];
          const hasAnswer = answer !== undefined && answer !== null && (Array.isArray(answer) ? answer.length > 0 : answer !== '');

          if (hasAnswer) {
            enrichedQuestions.push({
              id: q.id,
              question: q.question,
              type: q.answerType,
              choices: q.options || [],
              answer: answer,
            });
          }
        });
      }
    });

    const standardFieldsData = { ...data };
    questionIds.forEach(id => {
      delete standardFieldsData[id];
    });

    if (standardFieldsData.birthday) {
      const { day, month, year } = standardFieldsData.birthday;

      if (day && month && year) {
        standardFieldsData.birthday = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    const documentStep = flowData.steps.find(s => s.type === 'documents');

    if (documentStep) {
      const docRequests = getDocumentRequests(documentStep);

      const documents = [];

      docRequests.forEach((doc, di) => {
        const fileList = standardFieldsData[`documentFile_${di}`];

        if (fileList && fileList.length > 0) {
          const uploadedFile = fileList[0];

          let type = uploadedFile.type.split('/').shift();

          if (type === 'application') {
            type = 'document';
          }

          documents.push({
            file: uploadedFile,
            fileName: uploadedFile.name,
            fileType: type,
            documentCategory: doc?.documentType || '',
            documentDescription: standardFieldsData[`documentDescription_${di}`] || '',
            documentExpirationDate: standardFieldsData[`documentExpirationDate_${di}`] || null,
          });
        }
      });

      // ONLY if at least one document exists
      if (documents.length > 0) {
        setFilesToUpload(documents.map(doc => doc.file));
        standardFieldsData.documents = documents.map(doc => ({
          fileName: doc.fileName,
          fileType: doc.fileType,
          documentCategory: doc.documentCategory,
          documentDescription: doc.documentDescription,
          documentExpirationDate: doc.documentExpirationDate,
        }));
      }

      // cleanup
      docRequests.forEach((_, di) => {
        delete standardFieldsData[`documentFile_${di}`];
        delete standardFieldsData[`documentDescription_${di}`];
        delete standardFieldsData[`documentExpirationDate_${di}`];
      });
    }

    const agreementStep = flowData.steps.find(s => s.type === 'agreement');
    if (agreementStep && standardFieldsData.agreement_accepted) {
      standardFieldsData.agreementFileName = agreementStep.fileName || 'file.pdf';
      standardFieldsData.agreementFileTitle = agreementStep.fileTitle || 'Agreement Document';
      standardFieldsData.agreementFileId = agreementStep.fileId;
      standardFieldsData.agreementAccepted = standardFieldsData.agreement_accepted;
    }
    delete standardFieldsData.agreement_accepted;

    const finalPayload = {
      ...standardFieldsData,
      questionsData: JSON.stringify(enrichedQuestions),
    };

    if (agreementStep && agreementStep.requiresSignature) {
      const isEmpty = signatureMode === 'draw' ? !signatureRef.current || signatureRef.current.isEmpty() : !typedName.trim();

      if (isEmpty) {
        setSignatureError(true);
        return;
      }

      const dataUrl = signatureMode === 'draw' ? signatureRef.current.toDataURL('image/png') : await typedSignatureToDataUrl(typedName.trim());

      const blob = await (await fetch(dataUrl)).blob();
      const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });

      const uploadResult = await dispatch(
        getFlowSignatureUploadUrl({
          customerFlowId: flowData.id,
          agreementStepId: agreementStep.id,
        })
      );
      const { presignedUrl: sigPresignedUrl, signatureFileId } = uploadResult?.data || {};

      if (sigPresignedUrl) {
        await fetch(sigPresignedUrl, {
          method: 'PUT',
          body: signatureFile,
          headers: { 'Content-Type': 'image/png' },
        });
      }

      finalPayload.signatureFileId = signatureFileId;
      finalPayload.signedAt = new Date().toISOString();
      finalPayload.signatureMode = signatureMode;
    }

    console.log('DATA SENT', { ...finalPayload, customerFlowId: flowData.id, email });

    dispatch(updateCustomerDataFromInvitationFlow({ ...finalPayload, customerFlowId: flowData.id, email }));
  };

  const handleMultiChange = (fieldId, option, isChecked) => {
    const name = String(fieldId);
    const currentSelections = getValues(name);
    const safeSelections = Array.isArray(currentSelections) ? currentSelections : [];

    const newSelections = isChecked ? [...safeSelections, option] : safeSelections.filter(item => item !== option);

    setValue(name, newSelections, { shouldValidate: true });
  };

  const renderDocuments = () => {
    const docRequests = getDocumentRequests(currentStep);

    return (
      <div className="w-full space-y-6">
        {docRequests.map((doc, di) => {
          const fileFieldId = `documentFile_${di}`;
          const descFieldId = `documentDescription_${di}`;
          const dateFieldId = `documentExpirationDate_${di}`;
          const docName = doc.documentName || t('INVITE_FLOW.document', 'Document');
          const isReq = doc.documentRequired;

          const fileErrorMsg = errors[fileFieldId]?.message;
          const descriptionError = errors[descFieldId]?.message;
          const dateError = errors[dateFieldId]?.message;
          const isFileError = !!fileErrorMsg;

          const currentFiles = watch(fileFieldId);
          const hasFile = currentFiles && currentFiles.length > 0;

          return (
            <div key={di} className="w-full space-y-5">
              {docRequests.length > 1 && (
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t('INVITE_FLOW.document', 'Document')} {di + 1}
                </p>
              )}

              {/* FILE */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-900">
                    {docName} {isReq && <span className="text-red-500">*</span>}
                  </label>

                  {doc.documentType && (
                    <p className="text-xs text-gray-500 mt-0.5 capitalize">
                      {t('INVITE_FLOW.type', 'Type:')} {doc.documentType}
                    </p>
                  )}
                </div>

                {hasFile && (
                  <div className="mb-3 inline-flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg max-w-full gap-4">
                    <span className="text-sm text-gray-700 font-medium truncate">
                      {t('INVITE_FLOW.selectedFile', 'Selected file:')} {currentFiles[0].name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setValue(fileFieldId, undefined, { shouldValidate: true })}
                      className={`text-sm text-[${colors.mandy}] hover:underline whitespace-nowrap`}
                    >
                      {t('INVITE_FLOW.remove', 'Remove')}
                    </button>
                  </div>
                )}

                <input
                  type="file"
                  {...register(fileFieldId)}
                  className={`block text-sm text-gray-900 bg-gray-50 border ${
                    isFileError ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg cursor-pointer focus:border-[${colors.mandy}] focus:ring-[${colors.mandy}] ${hasFile ? 'hidden' : ''}`}
                />

                {isFileError && <p className="mt-2 text-sm text-red-600">{fileErrorMsg}</p>}
              </div>

              {/* DESCRIPTION */}
              {doc.includeDescription && (
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-900">
                    {t('INVITE_FLOW.description', 'Description')} {doc.descriptionRequired && <span className="text-red-500">*</span>}
                  </label>

                  <p className="text-xs text-gray-500 mb-2">
                    {t('INVITE_FLOW.provideDetails', 'Provide additional details or notes about the uploaded document')}
                  </p>

                  <textarea
                    {...register(descFieldId)}
                    rows={3}
                    placeholder={t('INVITE_FLOW.enterDescription', 'Enter description...')}
                    className={`w-full p-2.5 text-sm border ${
                      descriptionError ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg bg-gray-50 focus:border-[${colors.mandy}] focus:ring-[${colors.mandy}]`}
                  />

                  {descriptionError && <p className="mt-2 text-sm text-red-600">{descriptionError}</p>}
                </div>
              )}

              {/* EXPIRATION DATE */}
              {doc.includeExpirationDate && (
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-900">
                    {t('INVITE_FLOW.expirationDate', 'Expiration Date')} {doc.expirationDateRequired && <span className="text-red-500">*</span>}
                  </label>

                  <p className="text-xs text-gray-500 mb-2">{t('INVITE_FLOW.selectDocumentExpires', 'Select when this document expires')}</p>

                  <input
                    type="date"
                    {...register(dateFieldId)}
                    className={`w-80 p-2.5 text-sm border ${dateError ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-gray-50 focus:border-[${
                      colors.mandy
                    }] focus:ring-[${colors.mandy}] [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />

                  {dateError && <p className="mt-2 text-sm text-red-600">{dateError}</p>}
                </div>
              )}

              {di < docRequests.length - 1 && <hr className="border-gray-200" />}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStandardFields = () => {
    if (!currentStep.fields) return null;

    const DESIRED_ORDER = [
      // Personal / Billing Info
      'firstname',
      'lastname',
      'birthday',
      'phone',
      'gender',
      'country',
      'address',
      'codiceFiscale',
      'piva',
      'sdi',

      // Body Measurements
      'weight',
      'height',
      'leftBiceps',
      'rightBiceps',
      'leftLeg',
      'rightLeg',
      'leftCalf',
      'rightCalf',
      'waist',
      'leanMass',
      'boneMass',
      'visceralFat',
    ];

    const activeFieldIds = Object.keys(currentStep.fields).sort((a, b) => {
      const indexA = DESIRED_ORDER.indexOf(a);
      const indexB = DESIRED_ORDER.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA === -1 && indexB !== -1) return 1;
      if (indexB === -1 && indexA !== -1) return -1;
      return 0;
    });

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {activeFieldIds.map(fieldId => {
          const config = currentStep.fields[fieldId];
          const isReq = config.required;
          const errorMsg = errors[fieldId]?.message;
          const isError = !!errorMsg;

          const rawFieldLabel =
            stepTypeInfo.fields?.find(f => f.id === fieldId)?.label || fieldId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          const fieldLabel = t(`INVITE_FLOW.field_${fieldId}`, rawFieldLabel);

          let inputType = 'text';

          if (fieldId === 'phone') inputType = 'tel';
          if (
            ['weight', 'height', 'waist', 'leanMass', 'boneMass', 'visceralFat'].includes(fieldId) ||
            fieldId.includes('Biceps') ||
            fieldId.includes('Leg') ||
            fieldId.includes('Calf')
          ) {
            inputType = 'number';
          }

          const baseInputClass = `bg-gray-50 border ${
            isError ? 'border-red-500' : 'border-gray-300'
          } text-gray-900 text-sm rounded-lg focus:border-[${colors.mandy}] focus:ring-[${colors.mandy}] block w-full p-2.5`;

          if (fieldId === 'birthday') {
            const value = watch('birthday') || {};

            return (
              <div key={fieldId} className="sm:col-span-1">
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  {fieldLabel} {isReq && <span className="text-red-500">*</span>}
                </label>

                <div className="flex gap-2">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    placeholder="DD"
                    value={value.day || ''}
                    onChange={e => {
                      setValue('birthday', { ...value, day: e.target.value }, { shouldValidate: true });
                    }}
                    className={baseInputClass}
                  />

                  <input
                    type="number"
                    min={1}
                    max={12}
                    placeholder="MM"
                    value={value.month || ''}
                    onChange={e => {
                      setValue('birthday', { ...value, month: e.target.value }, { shouldValidate: true });
                    }}
                    className={baseInputClass}
                  />

                  <input
                    type="number"
                    placeholder="YYYY"
                    min={1900}
                    max={new Date().getFullYear()}
                    value={value.year || ''}
                    onChange={e => {
                      setValue('birthday', { ...value, year: e.target.value }, { shouldValidate: true });
                    }}
                    className={baseInputClass}
                  />
                </div>

                {errors.birthday?.message && <p className="mt-2 text-sm text-red-600">{errors.birthday.message}</p>}
              </div>
            );
          }

          return (
            <div key={fieldId} className={`sm:col-span-1 ${fieldId === 'address' ? 'sm:col-span-2' : ''}`}>
              <label className="block mb-2 text-sm font-medium text-gray-900">
                {fieldLabel} {isReq && <span className="text-red-500">*</span>}
              </label>

              {fieldId === 'gender' ? (
                <select {...register(fieldId)} className={baseInputClass}>
                  <option value="">{t('INVITE_FLOW.select', 'Select')}</option>
                  <option value="M">{t('INVITE_FLOW.male', 'Male')}</option>
                  <option value="F">{t('INVITE_FLOW.female', 'Female')}</option>
                  <option value="">{t('INVITE_FLOW.other', 'Other')}</option>
                </select>
              ) : (
                <input
                  type={inputType}
                  step={inputType === 'number' ? 'any' : undefined}
                  {...register(fieldId)}
                  className={`${baseInputClass} ${inputType === 'date' ? 'cursor-text [&::-webkit-calendar-picker-indicator]:cursor-pointer' : ''}`}
                  placeholder={`${t('INVITE_FLOW.enterYour', 'Enter your')} ${fieldLabel.toLowerCase()}`}
                />
              )}
              {isError && <p className="mt-2 text-sm text-red-600">{errorMsg}</p>}
            </div>
          );
        })}
      </div>
    );
  };

  const renderQuestions = () => {
    if (!currentStep.questions) return null;

    return (
      <div className="space-y-4">
        {currentStep.questions.map((q, index) => {
          const fieldName = String(q.id);
          const errorMsg = errors[fieldName]?.message;
          const isError = !!errorMsg;

          const value = watch(fieldName);
          const safeValue = Array.isArray(value) ? value : [];

          return (
            <div
              key={q.id}
              className={`bg-white p-4 rounded-xl border transition-all ${isError ? 'border-red-300 shadow-sm' : 'border-gray-200 shadow-sm'}`}
            >
              <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2.5">
                <span className={`text-[${colors.mandy}] mr-1.5`}>{index + 1}.</span>
                {q.question} {q.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {q.answerType === 'text' && (
                <textarea
                  rows="2"
                  {...register(fieldName)}
                  className={`bg-gray-50 border ${
                    isError ? 'border-red-500 focus:ring-red-500' : `border-gray-300 focus:ring-[${colors.mandy}]`
                  } text-gray-900 text-sm rounded-lg focus:border-[${colors.mandy}] focus:outline-none block w-full p-2.5 transition-colors`}
                  placeholder={t('INVITE_FLOW.typeYourAnswer', 'Type your answer here...')}
                />
              )}

              {q.answerType === 'single' && (
                <div className="flex flex-col gap-2">
                  {q.options.map((opt, i) => {
                    const isChecked = value === opt;

                    return (
                      <label
                        key={i}
                        className={`flex items-center gap-2.5 cursor-pointer border rounded-lg py-2 px-3 transition-all w-fit min-w-[200px] pr-6 ${
                          isChecked
                            ? `border-[${colors.mandy}] bg-[${colors.mandy}] bg-opacity-5`
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          value={opt}
                          {...register(fieldName)}
                          style={{
                            color: colors.mandy,
                            accentColor: colors.mandy,
                          }}
                          className={`w-4 h-4 border-gray-400 focus:ring-[${colors.mandy}] cursor-pointer`}
                        />
                        <span className={`text-sm ${isChecked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {q.answerType === 'multiple' && (
                <div className="flex flex-col gap-2">
                  {q.options.map((opt, i) => {
                    const isChecked = safeValue.includes(opt);
                    return (
                      <label
                        key={i}
                        className={`flex items-center gap-2.5 cursor-pointer border rounded-lg py-2 px-3 transition-all w-fit min-w-[200px] pr-6 ${
                          isChecked
                            ? `border-[${colors.mandy}] bg-[${colors.mandy}] bg-opacity-5`
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={e => handleMultiChange(fieldName, opt, e.target.checked)}
                          style={{
                            color: colors.mandy,
                            accentColor: colors.mandy,
                          }}
                          className={`w-4 h-4 border-2 border-gray-400 rounded-sm focus:ring-[${colors.mandy}] cursor-pointer`}
                        />
                        <span className={`text-sm ${isChecked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {isError && (
                <div className="mt-2.5 flex items-center gap-1.5 text-xs text-red-600 bg-red-50 py-1.5 px-2.5 rounded-md w-fit border border-red-100">
                  {errorMsg}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAgreement = () => {
    const fileTitle = currentStep.fileTitle || 'Agreement Document';
    const fileName = currentStep.fileName || 'file.pdf';
    const fieldId = 'agreement_accepted';

    const errorMsg = errors[fieldId]?.message;
    const isError = !!errorMsg;

    return (
      <div className="w-full max-w-xl">
        <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500">{t('INVITE_FLOW.document', 'Document')}</span>
            <span className="text-base font-semibold text-gray-900">{fileTitle}</span>
            <span className="text-xs text-gray-400 mt-1">{fileName}</span>
          </div>

          <div
            className={`text-sm font-medium text-[${colors.mandy}] cursor-pointer`}
            onClick={() => dispatch(getFileFromCustomerFlow(currentStep?.fileId || null, flowData.id))}
          >
            {t('INVITE_FLOW.preview', 'Preview')}
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-3">{t('INVITE_FLOW.reviewDocument', 'Please review the document before continuing.')}</p>

        <label className="mt-6 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            {...register(fieldId)}
            className={`mt-1 w-4 h-4 border-2 ${isError ? 'border-red-500' : 'border-gray-300'} rounded-sm accent-[${colors.mandy}] cursor-pointer`}
          />

          <span className="text-sm text-gray-700 leading-relaxed">
            {t('INVITE_FLOW.readAndAgreed', 'I have read and agreed to the terms in')}{' '}
            <span className={`font-medium text-[${colors.mandy}]`}>{fileTitle}</span>.
          </span>
        </label>
        {isError && <p className="mt-2 text-sm text-red-600">{errorMsg}</p>}

        {currentStep.requiresSignature && (
          <div className="mt-6">
            <p className="text-sm font-medium text-gray-900 mb-3">
              {t('INVITE_FLOW.signatureLabel')}
              <span className="text-red-500 ml-1">*</span>
            </p>

            <div className="flex gap-2 mb-4">
              {['draw', 'type'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setSignatureMode(mode);
                    setSignatureError(false);
                  }}
                  className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                    signatureMode === mode
                      ? `border-[${colors.mandy}] bg-[${colors.mandy}] text-white`
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {mode === 'draw' ? t('INVITE_FLOW.signatureDraw') : t('INVITE_FLOW.signatureType')}
                </button>
              ))}
            </div>

            {signatureMode === 'draw' && (
              <div>
                <p className="text-xs text-gray-500 mb-2">{t('INVITE_FLOW.signatureDrawHint')}</p>
                <div className={`border-2 rounded-lg bg-white touch-none ${signatureError ? 'border-red-500' : 'border-gray-300'}`}>
                  {/* <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    canvasProps={{ className: 'w-full', height: 180 }}
                    onBegin={() => setSignatureError(false)}
                  /> */}
                </div>
                <button
                  type="button"
                  onClick={() => signatureRef.current && signatureRef.current.clear()}
                  className="mt-1.5 text-xs text-gray-500 hover:underline"
                >
                  {t('INVITE_FLOW.clearSignature')}
                </button>
              </div>
            )}

            {signatureMode === 'type' && (
              <div>
                <p className="text-xs text-gray-500 mb-2">{t('INVITE_FLOW.signatureTypeHint')}</p>
                <input
                  type="text"
                  value={typedName}
                  onChange={e => {
                    setTypedName(e.target.value);
                    setSignatureError(false);
                  }}
                  placeholder={t('INVITE_FLOW.signatureTypePlaceholder')}
                  className={`w-full p-3 border-2 rounded-lg text-sm ${signatureError ? 'border-red-500' : 'border-gray-300'}`}
                />
                {typedName.trim() && (
                  <div
                    className="mt-3 p-4 border border-gray-200 rounded-lg bg-white text-center text-gray-900"
                    style={{ fontFamily: '"Dancing Script", cursive', fontSize: '2rem', lineHeight: 1.4 }}
                  >
                    {typedName}
                  </div>
                )}
              </div>
            )}

            {signatureError && <p className="mt-2 text-sm text-red-600">{t('INVITE_FLOW.signatureRequired')}</p>}
          </div>
        )}
      </div>
    );
  };

  const handleDownloadAttachment = async fileEntry => {
    try {
      console.log('FILE', fileEntry);
      if (fileEntry.url) {
        forceDownload(fileEntry.url, fileEntry.fileName);
        return;
      }

      const response = await getFileFromCustomerFlowApiCall({ fileId: fileEntry.fileId, customerFlowId: flowData.id });
      console.log('RESPONSE', response);
      const signedUrl = response.data.signedUrl;
      if (response && signedUrl) {
        forceDownload(signedUrl, fileEntry.fileName);
      }
    } catch (e) {
      console.error('Failed to download attachment', e);
    }
  };

  const forceDownload = async (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAttachments = () => {
    const files = currentStep.files || [];

    return (
      <div className="w-full space-y-4">
        <p className="text-sm text-gray-600">{t('INVITE_FLOW.attachmentsIntro')}</p>
        {files.map((fileEntry, i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <p className="text-base font-semibold text-gray-900 mb-3">{fileEntry.fileTitle}</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => dispatch(getFileFromCustomerFlow(fileEntry.fileId || null, flowData.id))}
                className={`text-sm font-medium text-[${colors.mandy}] hover:underline`}
              >
                {t('INVITE_FLOW.previewAttachment')}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadAttachment(fileEntry)}
                className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-1 hover:bg-gray-100"
              >
                {t('INVITE_FLOW.downloadAttachment')}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep.type === 'questions') return renderQuestions();
    if (currentStep.type === 'documents') return renderDocuments();
    if (currentStep.type === 'agreement') return renderAgreement();
    if (currentStep.type === 'attachments') return renderAttachments();
    return renderStandardFields();
  };

  if (updateSelector.status === StateStatus.ERROR) {
    return (
      <div className="mx-auto w-full max-w-4xl bg-white rounded-lg shadow-md p-4 sm:p-8">
        <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 rounded-lg">{t('INVITE_FLOW.somethingWentWrong', 'Something went wrong')}</div>
        <button
          type="button"
          onClick={() => history.push('/')}
          className={`bg-[${colors.mandy}] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg min-w-[200px]`}
        >
          {t('INVITE_FLOW.home', 'Home')}
        </button>
      </div>
    );
  }

  const isUploadComplete = filesToUpload.length > 0 ? stateIsLoaded(upload) : true;

  if (updateSelector.status === StateStatus.LOADED && isUploadComplete) {
    history.replace('/successFlow', {
      appConfig: flowData?.appConfig,
    });
    return null;
    // return <AppDownloadSuccess appConfig={flowData?.appConfig} onContinueWeb={() => history.push('/')} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto w-full max-w-4xl bg-white rounded-lg shadow-md p-4 sm:p-8">
      {(progressPercent || stateIsLoading([upload, updateSelector])) && (
        <FullScreenSpinner value={progressPercent} label={printPercent(progressPercent, null)} />
      )}
      <div className="flex gap-2 mb-8">
        {steps.map((_, idx) => (
          <div
            key={idx}
            className={`h-2 flex-1 rounded-full transition-colors duration-300 ${idx <= currentStepIndex ? `bg-[${colors.mandy}]` : 'bg-gray-200'}`}
          />
        ))}
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t(`INVITE_FLOW.stepTitle_${currentStep.type}`, stepTypeInfo.label)}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('INVITE_FLOW.step', 'Step')} {currentStepIndex + 1} {t('INVITE_FLOW.of', 'of')} {steps.length}
        </p>
      </div>

      <div>
        <div className="min-h-[250px]">{renderCurrentStep()}</div>

        <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStepIndex === 0}
            className={`px-6 py-3 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors ${
              currentStepIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {t('INVITE_FLOW.back', 'Back')}
          </button>

          {currentStepIndex === steps.length - 1 ? (
            <button
              key="submit-btn"
              type="submit"
              disabled={isSubmitting}
              className={`bg-[${colors.mandy}] text-white px-8 py-3 rounded-lg font-medium min-w-[200px] hover:opacity-90 transition-opacity disabled:opacity-50`}
            >
              {isSubmitting ? t('INVITE_FLOW.saving', 'Saving...') : t('INVITE_FLOW.finishAccount', 'Finish account')}
            </button>
          ) : (
            <button
              key="next-btn"
              type="button"
              onClick={e => {
                e.preventDefault();
                handleNext();
              }}
              className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors min-w-[150px]"
            >
              {t('INVITE_FLOW.next', 'Next')}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default FunctioningStepper;
